import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { downloadFile } from '../finance/financeUi';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const STAGES = ['new', 'contacted', 'qualified', 'application_sent', 'in_onboarding', 'declined'];
const EXPORT_FIELDS = [['business_name', 'Business'], ['contact_name', 'Contact'], ['email', 'Email'], ['phone', 'Phone'], ['street', 'Street'],
  ['city', 'City'], ['state', 'State'], ['zip', 'ZIP'], ['status', 'Stage'], ['source', 'Source'], ['tags', 'Tags']];

/** Bulk actions for selected leads. */
export function LeadBulkBar({ ids, onDone, canAssign }) {
  const [owners, setOwners] = useState([]);
  useEffect(() => { if (canAssign) api.request('/leads/import/owners').then(setOwners).catch(() => {}); }, [canAssign]);
  const run = async (action, value) => {
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} leads? This can't be undone.`)) return;
    try { const r = await api.request('/leads/bulk', { method: 'POST', body: JSON.stringify({ lead_ids: ids, action, value }) }); toast.success(`${r.affected} leads updated`); onDone(); }
    catch (e) { toast.error(e.message || 'Bulk action failed'); }
  };
  const tags = (action) => { const t = window.prompt(action === 'add_tags' ? 'Tags to add (comma separated)' : 'Tags to remove (comma separated)'); if (t) run(action, t.split(',').map((x) => x.trim()).filter(Boolean)); };
  const sel = 'h-9 rounded-md border bg-background px-2 text-sm';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-primary/5 p-3 text-sm" data-testid="lead-bulk-bar">
      <b data-testid="lead-bulk-count">{ids.length} selected</b>
      {canAssign && <select className={sel} defaultValue="" onChange={(e) => e.target.value && run('assign', e.target.value)} data-testid="lead-bulk-assign">
        <option value="" disabled>Assign owner…</option>{owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}
      <select className={sel} defaultValue="" onChange={(e) => e.target.value && run('stage', e.target.value)} data-testid="lead-bulk-stage">
        <option value="" disabled>Change stage…</option>{STAGES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select>
      <Button size="sm" variant="outline" onClick={() => tags('add_tags')} data-testid="lead-bulk-add-tags">Add tags</Button>
      <Button size="sm" variant="outline" onClick={() => tags('remove_tags')} data-testid="lead-bulk-remove-tags">Remove tags</Button>
      <Button size="sm" variant="outline" className="text-destructive" onClick={() => run('delete')} data-testid="lead-bulk-delete">Delete</Button>
    </div>
  );
}

export function LeadExportDialog({ status, onClose }) {
  const [fields, setFields] = useState(EXPORT_FIELDS.map(([k]) => k));
  const [custom, setCustom] = useState([]);
  useEffect(() => { api.request('/leads/custom-fields').then(setCustom).catch(() => {}); }, []);
  const all = [...EXPORT_FIELDS, ...custom.map((c) => [`custom.${c.key}`, c.label])];
  const go = (fmt) => downloadFile(`/leads/export?format=${fmt}&fields=${fields.join(',')}${status && status !== 'all' ? `&status=${status}` : ''}`, `leads.${fmt}`)
    .then(onClose).catch((e) => toast.error(e.message));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="lead-export-dialog">
        <DialogHeader><DialogTitle>Export leads</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-1 text-sm">{all.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={fields.includes(k)} data-testid={`export-field-${k}`}
            onChange={(e) => setFields((f) => (e.target.checked ? [...f, k] : f.filter((x) => x !== k)))} />{l}</label>))}</div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => go('csv')} disabled={!fields.length} data-testid="export-csv">CSV</Button>
          <Button onClick={() => go('xlsx')} disabled={!fields.length} data-testid="export-xlsx">Excel</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function ImportHistoryDialog({ onClose, onChanged }) {
  const [rows, setRows] = useState(null);
  const load = () => api.request('/leads/import/history').then(setRows).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const undo = async (r) => {
    if (!window.confirm(`Undo "${r.file_name}"? Leads it created are removed and leads it updated are restored.`)) return;
    try { const x = await api.request(`/leads/import/${r.import_id}/undo`, { method: 'POST' }); toast.success(`Removed ${x.removed}, restored ${x.restored}`); load(); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="import-history-dialog">
        <DialogHeader><DialogTitle>Import history</DialogTitle></DialogHeader>
        {!rows ? <p className="text-sm text-muted-foreground">Loading…</p> : !rows.length ? <p className="text-sm text-muted-foreground">No imports yet.</p> : (
          <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2">File</th><th>By</th><th>When</th><th>Counts</th><th /></tr></thead>
            <tbody>{rows.map((r) => (<tr key={r.import_id} className="border-b" data-testid={`import-history-${r.import_id}`}>
              <td className="py-2">{r.file_name}</td><td>{r.by}</td><td>{new Date(r.at).toLocaleString()}</td>
              <td className="text-xs">{r.status === 'UNDONE' ? 'Undone' : `${r.counts.imported || 0} new · ${r.counts.updated || 0} updated · ${r.counts.skipped || 0} skipped · ${r.counts.failed || 0} failed`}</td>
              <td className="text-right">{r.can_undo && <Button size="sm" variant="outline" onClick={() => undo(r)} data-testid={`import-undo-${r.import_id}`}>Undo</Button>}</td></tr>))}</tbody></table>)}
      </DialogContent>
    </Dialog>
  );
}
